import React, { useState, ChangeEvent } from 'react';
import notify from '@/lib/notify';
import Modal from '../Common/Modal'; // Assurez-vous que le chemin est correct
import { Persona } from '../../types'; // Décommenter l'import
import MarkdownDisplay from '../Common/MarkdownDisplay';
import personaService from '../../services/personaService';
import PersonaAvatar from '../Common/PersonaAvatar';
import { useTranslation } from 'react-i18next';

interface PersonaDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  persona: Persona | null;
  // Ajouter les callbacks pour les actions
  onEdit: (persona: Persona) => void;
  onDelete: (personaId: string) => Promise<void>; // Rendre async si l'appel API est ici
  // Callback optionnel pour rafraîchir la liste après mise à jour de l'avatar
  onAvatarUpdated?: (updatedPersona: Persona) => Promise<void> | void;
  // Indique si le modal est en lecture seule (pas d'édition/suppression)
  readOnly?: boolean;
}

const PersonaDetailModal: React.FC<PersonaDetailModalProps> = ({
    isOpen,
    onClose,
    persona,
    onEdit,
    onDelete,
    onAvatarUpdated,
    readOnly = false,
}) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);

  if (!persona) return null;

  const handleAvatarFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !persona) return;

    if (!file.type.startsWith('image/')) {
      notify.error(t('personaDetail.selectImage'));
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const updatedPersona = await personaService.uploadAvatar(persona.id, formData);

      notify.success(t('personaDetail.avatarUpdated'));
      if (onAvatarUpdated) {
        await onAvatarUpdated(updatedPersona);
      }
      // On peut vider la valeur de l'input pour permettre le même fichier à nouveau
      event.target.value = '';
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'avatar du persona:", error);
      notify.error(t('personaDetail.avatarError'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={t('personaDetail.title', { name: persona.name })}
      hideDefaultFooter
      // Largeur accrue pour un affichage plus confortable des informations du persona
      className="max-w-3xl"
      // Contenu scrollable, hauteur maximale fixe à 600px
      bodyClassName="max-h-[600px] overflow-y-auto space-y-4"
    >
      <div className="mt-4 space-y-4">
        {/* Avatar + action de mise à jour */}
        <div className="flex items-center space-x-4">
          <PersonaAvatar persona={persona} size="lg" />
          {onAvatarUpdated && !readOnly && (
            <div className="flex flex-col space-y-2">
              <label className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 cursor-pointer">
                <span>
                  {isUploading
                    ? t('personaDetail.uploadingAvatar', 'Téléchargement...')
                    : t('personaDetail.changeAvatar', 'Changer l’avatar')}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarFileChange}
                  disabled={isUploading}
                  className="hidden"
                />
              </label>
              <p className="text-xs text-gray-400">
                {t(
                  'personaDetail.avatarHelp',
                  'Formats image (PNG, JPG, etc.), taille raisonnable recommandée.',
                )}
              </p>
            </div>
          )}
        </div>

        {/* Utiliser les bons noms de champs */}
        <p><strong>{t('personaDetail.role')}</strong> {persona.role}</p>
        {persona.description && (
          <div>
            <strong>{t('personaDetail.description')}</strong>
            <div className="ml-4 mt-1">
              <MarkdownDisplay content={persona.description} />
            </div>
          </div>
        )}
        {persona.citation && (
          <div>
            <strong>{t('personaDetail.citation')}</strong>
            <div className="ml-4 mt-1">
              <MarkdownDisplay content={persona.citation} />
            </div>
          </div>
        )}
        <div>
          <strong>{t('personaDetail.objectives', 'Objectif(s) :')}</strong>
          <div className="ml-4 mt-1">
            <MarkdownDisplay content={persona.objectif} emptyMessage="-" />
          </div>
        </div>
         <div>
          <strong>{t('personaDetail.frustrations', 'Frustration(s) :')}</strong>
          <div className="ml-4 mt-1">
            <MarkdownDisplay content={persona.frustration} emptyMessage="-" />
          </div>
        </div>
        {/* Affichage complet pour les champs actuels */}
      </div>
      {/* Boutons d’actions */}
      <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
        {!readOnly && (
          <>
            <button
              type="button"
              onClick={() => {
                if (persona) {
                  onEdit(persona); // Appelle la fonction pour ouvrir la modale d'édition
                  onClose(); // Ferme la modale de détail
                }
              }}
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
            >
              {t('global.edit')}
            </button>
            <button
              type="button"
              onClick={async () => {
                if (persona && window.confirm(t('personaDetail.deleteConfirm', { name: persona.name }))) {
                  try {
                    await onDelete(persona.id);
                    onClose(); // Ferme la modale après suppression réussie
                  } catch (error) {
                    console.error("Erreur lors de la suppression du persona:", error);
                    notify.error(t('personaDetail.deleteError'));
                  }
                }
              }}
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            >
              {t('global.delete')}
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onClose}
          className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
        >
          {t('global.close')}
        </button>
      </div>
    </Modal>
  );
};

export default PersonaDetailModal;
